// Where a chat renders a Figma chip: under EVERY message that carried its link.
//
// The fold keeps one snapshot per link (one live state, one set of facts) and
// the list of messages the link rode in (`FigmaLinkSnapshot.placements`). This
// turns that into what the transcript renders. Pure, and type-only against
// `useWsSession`, which the FocusChat tests mock whole.
//
// A chip used to render once, under the NEWEST message that carried its link
// (N3): pasting the link again took the chip away from the earlier message the
// model's reply pointed at, and a message of four links kept "2 of 4 … 4 of 4"
// with no "1 of 4". Now each message keeps its chip, with the live state every
// copy shares and the place the link had in THAT message.

import type {
  FigmaLinkPlacement,
  FigmaLinkSnapshot,
} from "../../chat/useWsSession";

export interface FigmaChatPlacement {
  /** Echo seq of a rendered message → the chips under it, in message order. */
  readonly under: ReadonlyMap<number, ReadonlyArray<FigmaLinkSnapshot>>;
  /**
   * Links no rendered message carried: a replay from before `sourceSeq`, a
   * message this surface does not render, an empty transcript.
   */
  readonly foot: ReadonlyArray<FigmaLinkSnapshot>;
}

/** The link's placements, or the one its newest row names on a snapshot folded
 *  without the list (a hand-built snapshot, a test). */
const placementsOf = (
  link: FigmaLinkSnapshot,
): ReadonlyArray<FigmaLinkPlacement> => {
  if (link.placements !== undefined && link.placements.length > 0) {
    return link.placements;
  }
  if (link.sourceSeq === undefined) return [];
  return [
    {
      sourceSeq: link.sourceSeq,
      ...(link.inMessage !== undefined ? { inMessage: link.inMessage } : {}),
    },
  ];
};

/** The chip as it sits under one message: every live fact of the link, and
 *  that message's seq and place. */
const atPlacement = (
  link: FigmaLinkSnapshot,
  place: FigmaLinkPlacement,
): FigmaLinkSnapshot => {
  const { inMessage: _newest, ...live } = link;
  return {
    ...live,
    sourceSeq: place.sourceSeq,
    ...(place.inMessage !== undefined ? { inMessage: place.inMessage } : {}),
  };
};

/** Message order. A chip with no place keeps its fold order, after the rest. */
const byPlace = (a: FigmaLinkSnapshot, b: FigmaLinkSnapshot): number =>
  (a.inMessage?.index ?? Number.MAX_SAFE_INTEGER) -
  (b.inMessage?.index ?? Number.MAX_SAFE_INTEGER);

/**
 * Split the folded links across the messages on screen.
 *
 * @param anchors the echo seqs of the messages this surface renders.
 */
export const placeFigmaLinks = (
  links: ReadonlyArray<FigmaLinkSnapshot>,
  anchors: ReadonlySet<number>,
): FigmaChatPlacement => {
  const under = new Map<number, FigmaLinkSnapshot[]>();
  const foot: FigmaLinkSnapshot[] = [];
  for (const link of links) {
    let placed = false;
    for (const place of placementsOf(link)) {
      if (!anchors.has(place.sourceSeq)) continue;
      placed = true;
      const chip = atPlacement(link, place);
      const list = under.get(place.sourceSeq);
      if (list === undefined) under.set(place.sourceSeq, [chip]);
      else list.push(chip);
    }
    if (!placed) foot.push(link);
  }
  for (const list of under.values()) list.sort(byPlace);
  return { under, foot };
};
